/**
 * Leave policies API client. Admin surface for per-org overrides on
 * top of country leave defaults (ADR 0008 D2, PR 4a).
 */

import type {
  LeavePoliciesListResponse,
  LeaveType,
  LeaveTypeDefinitionRow,
  CreateLeaveTypeDefinitionInput,
  UpdateLeaveTypeDefinitionInput,
  UpdateLeavePolicyInput,
  UpdateLeaveSettingsInput,
  OrgLeaveSettings,
  EffectiveLeavePolicy,
} from '@kora/types';
import { apiRequest } from './client.js';

export function listLeavePolicies(): Promise<LeavePoliciesListResponse> {
  return apiRequest<LeavePoliciesListResponse>('/v1/leave-policies');
}

/** ADR-0023 Phase 2 — create a custom leave type. */
export function createCustomLeaveType(
  body: CreateLeaveTypeDefinitionInput,
): Promise<LeaveTypeDefinitionRow> {
  return apiRequest<LeaveTypeDefinitionRow>('/v1/leave-policies/custom-types', {
    method: 'POST',
    body,
  });
}

/** Rename (label) and/or retire (active) a custom type. `key` is immutable. */
export function updateCustomLeaveType(
  id: string,
  body: UpdateLeaveTypeDefinitionInput,
): Promise<LeaveTypeDefinitionRow> {
  return apiRequest<LeaveTypeDefinitionRow>(
    `/v1/leave-policies/custom-types/${encodeURIComponent(id)}`,
    { method: 'PATCH', body },
  );
}

/** Hard-delete a custom type (only when it has no history; else 409 — retire). */
export function deleteCustomLeaveType(id: string): Promise<void> {
  return apiRequest<void>(
    `/v1/leave-policies/custom-types/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

export interface UpdateLeavePolicyResult {
  override: {
    id: string;
    annualDaysOverride: number | null;
    maxCarryoverDaysOverride: number | null;
    minNoticeDaysOverride: number | null;
    effectiveFrom: string;
    effectiveTo: string | null;
  };
  effective: EffectiveLeavePolicy;
}

/** ADR-0022 C3 — set the org-wide public-holiday counting toggle. */
export function updateLeaveSettings(
  body: UpdateLeaveSettingsInput,
): Promise<{ settings: OrgLeaveSettings }> {
  return apiRequest<{ settings: OrgLeaveSettings }>('/v1/leave-policies/settings', {
    method: 'PATCH',
    body,
  });
}

export function updateLeavePolicy(args: {
  countryCode: string;
  leaveType: LeaveType;
  body: UpdateLeavePolicyInput;
}): Promise<UpdateLeavePolicyResult> {
  return apiRequest<UpdateLeavePolicyResult>(
    `/v1/leave-policies/${encodeURIComponent(args.countryCode)}/${encodeURIComponent(args.leaveType)}`,
    { method: 'PUT', body: args.body },
  );
}
